'use client';

import { memo, useMemo } from 'react';
import { LoadingSpinner } from '@crabs-ai/ds-react';
import { GiPaperWaterdrop } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import {
  FolderItemsResponseType,
  GetFolderItemsContentResponseType,
  GetFolderItemsFolderResponseType,
} from '@/shared/apis/folder/get-folder-items';
import { DtoFolderItemType } from '@/shared/constants';
import { AssetPickerCard } from './AssetPickerCard';

export interface AssetPickerGridProps {
  items: FolderItemsResponseType[];
  selectedItems: Map<number, GetFolderItemsContentResponseType>;
  isLoading: boolean;
  showTypeLabel?: boolean;
  isBookmarked: (itemId: number) => boolean;
  onSelect: (item: GetFolderItemsContentResponseType) => void;
  onFolderClick: (item: GetFolderItemsFolderResponseType) => void;
}

/**
 * 에셋 선택 모달용 그리드 컴포넌트
 * - 4열 그리드 레이아웃
 * - 폴더 우선 정렬
 */
export const AssetPickerGrid = memo(function AssetPickerGrid({
  items,
  selectedItems,
  isLoading,
  showTypeLabel = false,
  isBookmarked,
  onSelect,
  onFolderClick,
}: AssetPickerGridProps) {
  const t = useTranslations();

  // 폴더 우선 정렬 (폴더 -> 콘텐츠 순서)
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      // 폴더가 먼저
      if (
        a.type === DtoFolderItemType.folder &&
        b.type !== DtoFolderItemType.folder
      ) {
        return -1;
      }
      if (
        a.type !== DtoFolderItemType.folder &&
        b.type === DtoFolderItemType.folder
      ) {
        return 1;
      }
      return 0;
    });
  }, [items]);

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // 빈 상태
  if (sortedItems.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-sm">
        <GiPaperWaterdrop className="size-4xl [&_path]:fill-sys-cont-neutral-light" />
        <p className="text-body-medium text-sys-cont-neutral-default">
          {t('Editor.asset-picker.empty')}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="grid grid-cols-4 gap-xs p-xs">
        {sortedItems.map((item) => (
          <AssetPickerCard
            key={`${item.type}-${item.itemId}`}
            item={item}
            isSelected={
              item.type === DtoFolderItemType.content &&
              selectedItems.has(item.itemId)
            }
            isBookmarked={
              item.type === DtoFolderItemType.content &&
              isBookmarked(item.itemId)
            }
            showTypeLabel={showTypeLabel}
            onSelect={onSelect}
            onFolderClick={onFolderClick}
          />
        ))}
      </div>
    </div>
  );
});

export default AssetPickerGrid;
